import type { SocialPost } from './posts';
import { isInRange, type MsRange } from './window';

/**
 * Add each returned post, or replace the held copy when the returned one is
 * at least as fresh by `updatedAt`; an older copy (a response that crossed a
 * newer one in flight) is ignored. Returns the ids seen. The one rule for
 * writing a post into the cache, whether from a window, a by-id read or the
 * row a mutation returned.
 */
export const mergeNewer = (held: Map<string, SocialPost>, returned: SocialPost[]): Set<string> => {
  const ids = new Set<string>();
  for (const post of returned) {
    const current = held.get(post.id);
    if (!current || post.updatedAt >= current.updatedAt) held.set(post.id, post);
    ids.add(post.id);
  }
  return ids;
};

/**
 * Make the held posts inside `range` match what the API returned for it.
 * Rows are added, or replace the held copy when they are at least as fresh
 * by `updatedAt`. A held post inside the range that the response no longer
 * lists has moved (the API never deletes a post, a cancelled one stays
 * listed), so it is dropped and shows again when its new window is fetched.
 *
 * `fresh` says whether this response is newer than every response applied so
 * far. Windows overlap (adjacent month grids by up to two weeks, the minute
 * refresh inside the month) and their requests are not ordered, so a stale
 * response may land after a newer one that added or moved a post into the
 * overlap. A stale response still adds what it saw and still marks its
 * window loaded, but it drops nothing: only the newest snapshot may. A post
 * written after the request went out (`spare`) is likewise kept: the
 * response cannot know about it.
 *
 * Mutates `held`, which may be an observable map. Type-only import of
 * `SocialPost` on purpose: the runtime test loads this file under tsx, which
 * cannot load the platform registry's image assets.
 */
export const reconcilePosts = (
  held: Map<string, SocialPost>,
  range: MsRange,
  returned: SocialPost[],
  fresh: boolean,
  /** Ids written into `held` after this response's request was sent (a mutation, a by-id read): the response predates them, so they are kept even when it does not list them. */
  spare?: ReadonlySet<string>,
): void => {
  const ids = mergeNewer(held, returned);
  if (!fresh) return;
  for (const post of held.values()) {
    if (isInRange(post.publishAt, range) && !ids.has(post.id) && !spare?.has(post.id)) {
      held.delete(post.id);
    }
  }
};
